/**
 * Utilidades para validar blisters y sus precios
 */

/**
 * Precio unitario efectivo para el usuario actual (rol + descuento).
 * Prioriza final_price del API; si no existe, calcula desde price base.
 */
export const getBlisterUnitPrice = (blister) => {
  if (!blister) return 0

  if (blister.final_price != null && blister.final_price > 0) {
    return parseFloat(blister.final_price)
  }

  return calculateDiscountedPrice(blister)
}

/**
 * Verifica si un blister tiene precio visible para el usuario
 */
export const hasBlisterPrice = (blister) => getBlisterUnitPrice(blister) > 0

/**
 * Verifica si un blister es válido para agregar al carrito
 * @param {Object} blister - El objeto blister a validar
 * @returns {Object} - { isValid: boolean, reason: string }
 */
export const validateBlisterForCart = (blister) => {
  if (!blister) {
    return { isValid: false, reason: 'No se ha seleccionado una presentación' }
  }

  if (!hasBlisterPrice(blister)) {
    return { isValid: false, reason: 'Este producto no tiene precio asignado' }
  }

  if (blister.stock <= 0) {
    return { isValid: false, reason: 'No hay stock disponible' }
  }

  return { isValid: true, reason: '' }
}

/**
 * Filtra blisters que son válidos para mostrar
 * @param {Array} blisters - Array de blisters
 * @returns {Array} - Array de blisters válidos
 */
export const getValidBlisters = (blisters) => {
  if (!Array.isArray(blisters)) return []

  return blisters.filter(hasBlisterPrice)
}

/**
 * Obtiene el primer blister disponible (priorizando con stock, pero permitiendo sin stock)
 * @param {Array} blisters - Array de blisters
 * @returns {Object|null} - El primer blister disponible o null
 */
export const getFirstAvailableBlister = (blisters) => {
  if (!Array.isArray(blisters)) return null

  const availableWithStock = blisters.find(b => b.stock > 0 && hasBlisterPrice(b))
  if (availableWithStock) return availableWithStock

  const availableWithPrice = blisters.find(hasBlisterPrice)
  if (availableWithPrice) return availableWithPrice

  return blisters.length > 0 ? blisters[0] : null
}

/**
 * Verifica si un blister debe estar deshabilitado en el dropdown
 * @param {Object} blister - El objeto blister
 * @returns {boolean} - true si debe estar deshabilitado
 */
export const isBlisterDisabled = (blister) => {
  return !blister || !hasBlisterPrice(blister)
}

/**
 * Obtiene el texto de estado de un blister
 * @param {Object} blister - El objeto blister
 * @returns {string} - Texto descriptivo del estado
 */
export const getBlisterStatusText = (blister) => {
  if (!blister) return ''
  if (!hasBlisterPrice(blister)) return ''
  if (blister.stock <= 0) return ''
  return ''
}

/**
 * Verifica si se debe mostrar el precio de un blister
 * @param {Object} blister - El objeto blister
 * @returns {boolean} - true si se debe mostrar el precio
 */
export const shouldShowPrice = (blister) => hasBlisterPrice(blister)

/**
 * Calcula el precio con descuento (precio base del blister, sin reglas de rol)
 * @param {Object} blister - El objeto blister
 * @returns {number} - Precio final con descuento aplicado
 */
export const calculateDiscountedPrice = (blister) => {
  if (!blister || !blister.price || blister.price <= 0) return 0

  if (!blister.discount || blister.discount <= 0) {
    return parseFloat(blister.price)
  }

  return parseFloat(blister.price * (1 - blister.discount / 100))
}
